import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, AlertTriangle, AlertOctagon, Info, Trash2, RefreshCw, Filter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDateTime } from "../lib/date";

const FILTERS = [
  { value: "all", label: "Todas" },
  { value: "unread", label: "Sin leer" },
  { value: "kiosk_offline", label: "Kioskos" },
  { value: "incident_pending", label: "Incidencias" },
  { value: "doc_expiring", label: "Documentos" },
  { value: "payroll_pending", label: "Planilla" },
  { value: "attendance_anomaly", label: "Anomalías" },
];

function SeverityIcon({ severity, className = "w-5 h-5" }) {
  if (severity === "critical") return <AlertOctagon className={`${className} text-red-600`} strokeWidth={1.6} />;
  if (severity === "warning") return <AlertTriangle className={`${className} text-amber-500`} strokeWidth={1.6} />;
  return <Info className={`${className} text-zinc-500`} strokeWidth={1.6} />;
}

function fmtRel(iso) {
  if (!iso) return "";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es }); }
  catch { return ""; }
}
function fmtAbs(iso) {
  return formatDateTime(iso, "");
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter === "unread") params.unread_only = true;
      else if (filter !== "all") params.category = filter;
      const { data } = await api.get("/notifications", { params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const unreadCount = items.filter((n) => !n.read).length;

  const handleMarkAll = async () => {
    try {
      const { data } = await api.post("/notifications/mark-all-read");
      toast.success(`${data.updated || 0} notificaciones marcadas como leídas`);
      await load();
    } catch {
      toast.error("Error al marcar como leídas");
    }
  };

  const handleClearRead = async () => {
    try {
      const { data } = await api.post("/notifications/clear-read");
      toast.success(`${data.deleted || 0} notificaciones eliminadas`);
      await load();
    } catch {
      toast.error("Error al limpiar");
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/notifications/scan-all");
      const total = (data.kiosks || 0) + (data.incidents || 0) + (data.documents || 0) + (data.payrolls || 0) + (data.anomalies || 0);
      if (total > 0) toast.success(`${total} nuevas alertas (${data.kiosks} kioskos · ${data.incidents} incidencias · ${data.documents} documentos · ${data.payrolls} planillas · ${data.anomalies || 0} anomalías)`);
      else toast("Sin novedades");
      await load();
    } catch {
      toast.error("Error al escanear");
    } finally {
      setScanning(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const handleOpen = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  return (
    <div data-testid="notifications-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">Notificaciones</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Alertas del sistema, kioskos sin conexión y eventos importantes
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleScan}
            disabled={scanning}
            data-testid="notifications-scan-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${scanning ? "animate-spin" : ""}`} />
            Revisar todo
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAll}
              data-testid="notifications-mark-all-page"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-2" /> Marcar todo como leído
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearRead}
            className="text-zinc-500 hover:text-red-600"
            data-testid="notifications-clear-read"
          >
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Borrar leídas
          </Button>
        </div>
      </div>

      <div className="mb-5 flex items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-zinc-400" />
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-zinc-100">
            {FILTERS.map((f) => (
              <TabsTrigger
                key={f.value}
                value={f.value}
                className="text-xs"
                data-testid={`notifications-filter-${f.value}`}
              >
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-400">Cargando notificaciones…</div>
      ) : items.length === 0 ? (
        <Card className="py-16 px-6 text-center border-dashed">
          <Bell className="w-10 h-10 text-zinc-300 mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-base font-medium text-zinc-700">No hay notificaciones</p>
          <p className="text-sm text-zinc-500 mt-1">
            {filter === "unread" ? "Todas están leídas." : "Pronto aparecerán alertas y avisos aquí."}
          </p>
        </Card>
      ) : (
        <Card className="divide-y divide-zinc-100 overflow-hidden">
          {items.map((n) => (
            <div
              key={n.id}
              className={`p-4 sm:p-5 flex gap-3 sm:gap-4 items-start group hover:bg-zinc-50/60 ${n.read ? "" : "bg-amber-50/30"}`}
              data-testid={`notification-row-${n.id}`}
            >
              <div className="mt-0.5 shrink-0">
                <SeverityIcon severity={n.severity} />
              </div>
              <button
                type="button"
                onClick={() => handleOpen(n)}
                className="flex-1 min-w-0 text-left"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`text-sm leading-tight ${n.read ? "text-zinc-700 font-medium" : "text-zinc-900 font-semibold"}`}>
                    {n.title}
                  </p>
                  {!n.read && (
                    <span className="inline-block px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold uppercase tracking-wide">
                      Nueva
                    </span>
                  )}
                </div>
                <p className="text-sm text-zinc-600 mt-1 leading-snug">{n.message}</p>
                <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500">
                  <span title={fmtAbs(n.created_at)}>{fmtRel(n.created_at)}</span>
                  {n.meta?.branch_name && (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-zinc-300" />
                      {n.meta.branch_name}
                      {n.meta.branch_code ? ` (${n.meta.branch_code})` : ""}
                    </span>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleDelete(n.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-600 p-1"
                aria-label="Eliminar notificación"
                data-testid={`notification-delete-${n.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
